import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

export type CalendarProvider = 'google' | 'microsoft' | 'square' | 'wix'

export interface AssignedAIAgent {
  id: number
  name: string
}

export interface ConnectedCalendar {
  id: number
  provider: CalendarProvider
  calendar_name: string
  email: string
  calendar_id?: string
  is_active: boolean
  last_sync_at: string | null
  token_expires_at?: string
  assigned_agents: AssignedAIAgent[]
  created_at: string
  /** Square seulement : ce que l'agent réserve. */
  square?: SquareSettings
  /** Wix only: what the agent books on the Wix site. */
  wix?: WixSettings
}

export interface SquareSettings {
  location_id: string
  location_name: string
  team_member_id: string
  team_member_name: string
  service_variation_id: string
  service_name: string
  service_duration_minutes: number
  /** Les services lus dans Square ; service_variation_id vide = l'agent réserve celui que le client demande. */
  services: SquareService[]
  /** Dernier refus de Square (code square_*), vide si tout va bien. */
  last_error: string
}

export interface SquareService {
  id: string
  version: number
  name: string
  duration_minutes: number
  price: number | null
  currency: string | null
  team_member_ids: string[]
}

export interface SquareOptions {
  locations: Array<{ id: string; name: string; timezone: string }>
  team_members: Array<{ id: string; name: string }>
  services: SquareService[]
}

/** How a Wix service is paid. 'en_ligne' and 'forfait' go through the site's checkout. */
export type WixPayment = 'sur_place' | 'gratuit' | 'en_ligne' | 'forfait'

export interface WixService {
  id: string
  name: string
  duration_minutes: number
  price: number | null
  currency: string | null
  paiement: WixPayment
  /** The business approves each request itself in Wix. */
  approbation: boolean
  schedule_id: string
  staff: Array<{ id: string; name: string }>
}

export interface WixSettings {
  /** Name of the Wix site. */
  business_name: string
  site_url: string
  /** Empty: the agent offers every service and books the one the caller asks for. */
  service_id: string
  service_name: string
  service_duration_minutes: number
  /** Empty: anyone on the team. */
  staff_resource_id: string
  staff_name: string
  services: WixService[]
  /** Last refusal from Wix (wix_* code), empty when all is well. */
  last_error: string
}

export interface WixOptions {
  services: WixService[]
  staff: Array<{ id: string; name: string }>
}

/** A service paid online (or with a plan) cannot be booked over the phone. */
export const wixPaidOnline = (s: Pick<WixService, 'paiement'>) =>
  s.paiement === 'en_ligne' || s.paiement === 'forfait'

/** The Callizy app was removed from the site, or the site is inactive: Wix must be reconnected. */
export const wixDisconnected = (cal: Pick<ConnectedCalendar, 'provider' | 'is_active' | 'wix'>) =>
  cal.provider === 'wix' && (!cal.is_active || cal.wix?.last_error === 'wix_app_removed')

interface RawCalendar {
  id: number
  calendar_name: string
  is_active?: boolean
  last_sync_at?: string | null
  token_expires_at?: string
  assigned_agents?: AssignedAIAgent[]
  created_at: string
  calendar_id?: string
}

interface GoogleCalendarsResponse {
  calendars: Array<RawCalendar & { google_email: string }>
}

interface MicrosoftCalendarsResponse {
  calendars: Array<RawCalendar & { microsoft_email: string }>
}

interface SquareBookingsResponse {
  calendars: Array<RawCalendar & SquareSettings & { business_name: string }>
}

interface WixBookingsResponse {
  calendars: Array<RawCalendar & WixSettings>
}

const ENDPOINTS: Record<CalendarProvider, string> = {
  google: 'google-calendars',
  microsoft: 'microsoft-calendars',
  square: 'square-bookings',
  wix: 'wix-bookings',
}

const endpointFor = (companyId: number | undefined, provider: CalendarProvider) =>
  `/api/companies/${companyId}/${ENDPOINTS[provider]}/`

/**
 * Récupère tous les calendriers connectés (Google + Microsoft) pour une entreprise.
 * Les deux providers sont chargés en parallèle ; un provider en échec n'invalide pas l'autre.
 */
export function useCalendars(companyId: number | undefined) {
  return useQuery({
    queryKey: ['connected-calendars', companyId],
    queryFn: async () => {
      const [googleRes, microsoftRes, squareRes, wixRes] = await Promise.allSettled([
        api.get<GoogleCalendarsResponse>(
          `/api/companies/${companyId}/google-calendars/`,
        ),
        api.get<MicrosoftCalendarsResponse>(
          `/api/companies/${companyId}/microsoft-calendars/`,
        ),
        api.get<SquareBookingsResponse>(
          `/api/companies/${companyId}/square-bookings/`,
        ),
        api.get<WixBookingsResponse>(
          `/api/companies/${companyId}/wix-bookings/`,
        ),
      ])

      const out: ConnectedCalendar[] = []
      if (googleRes.status === 'fulfilled') {
        for (const c of googleRes.value.data.calendars) {
          out.push({
            id: c.id,
            provider: 'google',
            calendar_name: c.calendar_name,
            email: c.google_email,
            calendar_id: c.calendar_id,
            is_active: c.is_active ?? true,
            last_sync_at: c.last_sync_at ?? null,
            token_expires_at: c.token_expires_at,
            assigned_agents: c.assigned_agents ?? [],
            created_at: c.created_at,
          })
        }
      }
      if (microsoftRes.status === 'fulfilled') {
        for (const c of microsoftRes.value.data.calendars) {
          out.push({
            id: c.id,
            provider: 'microsoft',
            calendar_name: c.calendar_name,
            email: c.microsoft_email,
            calendar_id: c.calendar_id,
            is_active: c.is_active ?? true,
            last_sync_at: c.last_sync_at ?? null,
            token_expires_at: c.token_expires_at,
            assigned_agents: c.assigned_agents ?? [],
            created_at: c.created_at,
          })
        }
      }
      if (squareRes.status === 'fulfilled') {
        for (const c of squareRes.value.data.calendars) {
          out.push({
            id: c.id,
            provider: 'square',
            calendar_name: c.calendar_name,
            email: c.business_name,
            calendar_id: c.location_id,
            is_active: c.is_active ?? true,
            last_sync_at: null,
            token_expires_at: c.token_expires_at,
            assigned_agents: c.assigned_agents ?? [],
            created_at: c.created_at,
            square: {
              location_id: c.location_id,
              location_name: c.location_name,
              team_member_id: c.team_member_id,
              team_member_name: c.team_member_name,
              service_variation_id: c.service_variation_id,
              service_name: c.service_name,
              service_duration_minutes: c.service_duration_minutes,
              services: c.services ?? [],
              last_error: c.last_error ?? '',
            },
          })
        }
      }
      if (wixRes.status === 'fulfilled') {
        for (const c of wixRes.value.data.calendars) {
          out.push({
            id: c.id,
            provider: 'wix',
            calendar_name: c.calendar_name,
            // The "account" of a Wix calendar is the site.
            email: c.business_name || c.site_url,
            is_active: c.is_active ?? true,
            last_sync_at: c.last_sync_at ?? null,
            assigned_agents: c.assigned_agents ?? [],
            created_at: c.created_at,
            wix: {
              business_name: c.business_name,
              site_url: c.site_url,
              service_id: c.service_id,
              service_name: c.service_name,
              service_duration_minutes: c.service_duration_minutes,
              staff_resource_id: c.staff_resource_id,
              staff_name: c.staff_name,
              services: c.services ?? [],
              last_error: c.last_error ?? '',
            },
          })
        }
      }
      // Plus récent en premier (pattern Next).
      out.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      )
      return out
    },
    enabled: !!companyId,
    staleTime: 30_000,
  })
}

/**
 * POST /api/companies/{id}/{provider}-calendars/auth-url/
 * Retourne l'URL OAuth vers Google/Microsoft ; le caller redirige le navigateur.
 */
export function useConnectCalendar(companyId: number | undefined) {
  return useMutation({
    mutationFn: async (input: {
      provider: CalendarProvider
      calendar_name: string
    }) => {
      const endpoint = `${endpointFor(companyId, input.provider)}auth-url/`
      const { data } = await api.post<{ auth_url: string }>(endpoint, {
        calendar_name: input.calendar_name,
      })
      return data.auth_url
    },
  })
}

/** DELETE /api/companies/{id}/{provider}-calendars/{calendarId}/ */
export function useDisconnectCalendar(companyId: number | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      provider: CalendarProvider
      calendarId: number
    }) => {
      await api.delete(`${endpointFor(companyId, input.provider)}${input.calendarId}/`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['connected-calendars', companyId],
      })
    },
  })
}

/** GET .../square-bookings/{id}/options/ : emplacements, membres et services du compte Square. */
export function useSquareOptions(companyId: number | undefined, calendarId: number | null, locationId?: string) {
  return useQuery({
    queryKey: ['square-options', companyId, calendarId, locationId ?? ''],
    queryFn: async () => {
      const { data } = await api.get<SquareOptions>(
        `/api/companies/${companyId}/square-bookings/${calendarId}/options/`,
        { params: locationId ? { location_id: locationId } : undefined },
      )
      return data
    },
    enabled: !!companyId && !!calendarId,
    staleTime: 60_000,
  })
}

/** PATCH .../square-bookings/{id}/ : emplacement, membre, service, nom. */
export function useUpdateSquareBooking(companyId: number | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      calendarId: number
      calendar_name?: string
      location_id?: string
      team_member_id?: string
      service_variation_id?: string
      is_active?: boolean
    }) => {
      const { calendarId, ...body } = input
      const { data } = await api.patch<{ calendar: SquareSettings & { calendar_name: string } }>(
        `/api/companies/${companyId}/square-bookings/${calendarId}/`,
        body,
      )
      return data.calendar
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connected-calendars', companyId] })
    },
  })
}

/** GET .../wix-bookings/{id}/options/ : services and team members of the Wix site. */
export function useWixOptions(companyId: number | undefined, calendarId: number | null) {
  return useQuery({
    queryKey: ['wix-options', companyId, calendarId],
    queryFn: async () => {
      const { data } = await api.get<WixOptions>(
        `/api/companies/${companyId}/wix-bookings/${calendarId}/options/`,
      )
      return data
    },
    enabled: !!companyId && !!calendarId,
    staleTime: 60_000,
    retry: false,
  })
}

/** PATCH .../wix-bookings/{id}/ : service ('' = all), team member ('' = anyone), name, state. */
export function useUpdateWixBooking(companyId: number | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      calendarId: number
      calendar_name?: string
      service_id?: string
      staff_resource_id?: string
      is_active?: boolean
    }) => {
      const { calendarId, ...body } = input
      const { data } = await api.patch<{ success: boolean; calendar: WixSettings & { calendar_name: string } }>(
        `/api/companies/${companyId}/wix-bookings/${calendarId}/`,
        body,
      )
      return data.calendar
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connected-calendars', companyId] })
    },
  })
}

/** The server answers 503 to auth-url while Wix is not set up on its side yet. */
export const isConnectUnavailable = (error: unknown) =>
  (error as { response?: { status?: number } })?.response?.status === 503
